/**
 * Every channel Tola can talk on, connected or not.
 *
 * Both the Settings navigation and the All channels page are built from this
 * list rather than from the inboxes that happen to exist, because a channel you
 * have *not* connected is exactly the one those surfaces need to tell you about.
 * `channelType` matches `inbox.channel_type`; `key` is the `sub_page` param of
 * the setup route. `recommended` entries make up the row the All channels page
 * leads with.
 *
 * Channel types this catalogue omits on purpose (voice, TikTok, Line, Twitter)
 * are hidden from every settings surface at once; the flag-gated entries below
 * are filtered per account by `availableChannels`.
 */
export const CHANNEL_CATALOGUE = [
  {
    key: 'website',
    channelType: 'Channel::WebWidget',
    labelKey: 'CHANNELS.TYPES.LIVE_CHAT',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION',
    icon: 'i-ph-globe',
    recommended: true,
  },
  {
    key: 'email',
    channelType: 'Channel::Email',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION',
    icon: 'i-ph-envelope-simple',
    recommended: true,
  },
  {
    key: 'whatsapp',
    channelType: 'Channel::Whatsapp',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.DESCRIPTION',
    icon: 'i-ph-whatsapp-logo',
    recommended: true,
  },
  {
    key: 'facebook',
    channelType: 'Channel::FacebookPage',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION',
    icon: 'i-ph-messenger-logo',
  },
  {
    key: 'sms',
    channelType: 'Channel::TwilioSms',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.DESCRIPTION',
    icon: 'i-ph-chat-dots',
  },
  {
    key: 'telegram',
    channelType: 'Channel::Telegram',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION',
    icon: 'i-ph-telegram-logo',
  },
  {
    key: 'instagram',
    channelType: 'Channel::Instagram',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION',
    icon: 'i-ph-instagram-logo',
  },
  {
    key: 'api',
    channelType: 'Channel::Api',
    labelKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE',
    descriptionKey: 'INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION',
    icon: 'i-ph-code',
  },
];

/**
 * Which catalogue entries this account can actually connect - the same gates
 * the inbox-creation picker (`ChannelItem`) enforces: an account feature flag
 * per first-party channel, plus the installation-level app id for the Meta
 * channels. WhatsApp, SMS, Telegram and API are ungated there and stay ungated
 * here.
 */
const CHANNEL_GATES = {
  website: features => features.channel_website,
  email: features => features.channel_email,
  facebook: features => features.channel_facebook && window.tolaConfig?.fbAppId,
  instagram: features =>
    features.channel_instagram && window.tolaConfig?.instagramAppId,
};

export const availableChannels = (features = {}) =>
  CHANNEL_CATALOGUE.filter(channel => {
    const gate = CHANNEL_GATES[channel.key];
    return gate ? Boolean(gate(features)) : true;
  });

export const inboxesForChannel = (inboxes, channelType) =>
  (inboxes ?? []).filter(inbox => inbox.channel_type === channelType);
